import { type FormEvent, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Badge, Loader } from '@lms/ui';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { listAllResource, listResource } from '@/lib/api';
import { studentProfilePath } from '@/lib/user-detail-navigation';

interface ProgressRecord {
  id: string;
  userId?: string;
  courseId?: string;
  progressPercent?: number;
  status?: string;
  lastAccessedAt?: string;
  user?: { fullName?: string; name?: string; email?: string; avatarUrl?: string };
  course?: { name: string; trashed?: boolean };
}

interface CourseOption {
  id: string;
  name: string;
}

const STATUS_LABELS: Record<string, string> = {
  not_started: 'No iniciado',
  in_progress: 'En progreso',
  completed: 'Completado',
  approved: 'Aprobado',
  failed: 'Reprobado',
  pending: 'Pendiente',
  blocked: 'Bloqueado',
  skipped: 'Omitido',
};

function statusVariant(status?: string): 'default' | 'primary' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'in_progress':
      return 'primary';
    case 'completed':
    case 'approved':
      return 'success';
    case 'failed':
      return 'danger';
    case 'pending':
    case 'blocked':
      return 'warning';
    default:
      return 'default';
  }
}

function barMod(status?: string): string {
  if (status === 'completed' || status === 'approved') return 'completed';
  if (status === 'in_progress') return 'in-progress';
  return 'default';
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

export function ProgresoPage() {
  const location = useLocation();
  const { hasAnyPermission } = usePermissions();
  const canViewProfile = hasAnyPermission(PERMISSIONS.USUARIOS_VER, PERMISSIONS.ESTUDIANTES_PERFIL_VER);
  const profileFrom = `${location.pathname}${location.search}`;
  const [courseId, setCourseId] = useState('');
  const [studentNameQuery, setStudentNameQuery] = useState('');
  const appliedSearch = useDebouncedValue(studentNameQuery, 300).trim();

  const { data, isLoading, error } = useQuery({
    queryKey: ['progress', 'all', appliedSearch],
    queryFn: () =>
      listAllResource<ProgressRecord>('/progress', appliedSearch ? { search: appliedSearch } : undefined),
  });

  const { data: coursesData } = useQuery({
    queryKey: ['courses', 'progress-filter'],
    queryFn: () => listResource<CourseOption>('/courses'),
  });

  const records = useMemo(() => {
    const rows = data?.data ?? [];
    if (!courseId) return rows;
    return rows.filter((record) => record.courseId === courseId);
  }, [data?.data, courseId]);

  const courses = coursesData?.data ?? [];
  const hasFilters = Boolean(courseId || studentNameQuery.trim());

  return (
    <div className="lms-progress-admin lms-site-admin-hub-page font-sans">
      <header className="flex items-start gap-4">
        <HubBackLink to="/administracion?tab=grades" label="Volver a Administración" />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Progreso de aprendizaje</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Vista administrativa del avance de estudiantes por curso
          </p>
        </div>
      </header>

      <section className="lms-progress-admin__filters">
        <form method="get" action="/progreso" className="lms-progress-admin__filters-grid" onSubmit={(event) => event.preventDefault()}>
          <div className="lms-progress-admin__field lms-progress-admin__field--course">
            <label htmlFor="progress-course-filter">Curso</label>
            <div className="lms-progress-admin__select-wrap">
              <select
                id="progress-course-filter"
                name="course_id"
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
              >
                <option value="">Seleccionar curso</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <span className="material-symbols-outlined" aria-hidden="true">
                expand_more
              </span>
            </div>
          </div>

          <div className="lms-progress-admin__field lms-progress-admin__field--user">
            <label htmlFor="progress-student-filter">Nombre del alumno</label>
            <SearchInput
              id="progress-student-filter"
              name="student_name"
              value={studentNameQuery}
              onChange={setStudentNameQuery}
              placeholder="Ej: Alumno Smoke"
              aria-label="Nombre del alumno"
            />
          </div>

          <div className="lms-progress-admin__actions">
            {hasFilters ? (
              <Link
                to="/progreso"
                className="lms-progress-admin__btn lms-progress-admin__btn--ghost"
                onClick={() => {
                  setCourseId('');
                  setStudentNameQuery('');
                }}
              >
                Limpiar
              </Link>
            ) : (
              <span className="lms-progress-admin__btn lms-progress-admin__btn--ghost lms-progress-admin__btn--muted">
                Limpiar
              </span>
            )}
          </div>
        </form>
      </section>

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      <div className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : records.length === 0 ? (
            <div className="lms-gradebook__table-wrap">
              <div className="lms-gradebook__empty">
                <div className="lms-gradebook__empty-icon" aria-hidden="true">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    monitoring
                  </span>
                </div>
                <h3>Sin registros</h3>
                <p>
                  {hasFilters
                    ? 'No se encontró progreso con los filtros aplicados.'
                    : 'No hay progreso de estudiantes registrado.'}
                </p>
                {hasFilters ? (
                  <button
                    type="button"
                    className="lms-gradebook__clear"
                    onClick={() => {
                      setCourseId('');
                      setStudentNameQuery('');
                    }}
                  >
                    Restablecer filtros
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <div className="lms-gradebook__table-wrap">
                <table className="lms-gradebook__table">
                  <thead>
                    <tr>
                      <th className="lms-gradebook__th" scope="col">
                        Estudiante
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Curso
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Progreso
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Estado
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Último acceso
                      </th>
                      <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record) => {
                      const name = record.user?.fullName ?? record.user?.name ?? 'Usuario';
                      const percent = Math.min(100, Math.max(0, record.progressPercent ?? 0));
                      const rounded = Math.round(percent);
                      const lastAccess = record.lastAccessedAt ? new Date(record.lastAccessedAt) : null;
                      const canDetail = Boolean(
                        record.userId && record.courseId && record.course && !record.course.trashed,
                      );
                      const profileTo =
                        canViewProfile && record.userId ? studentProfilePath(record.userId, profileFrom) : null;
                      const nameTo = canDetail
                        ? `/progreso/${record.userId}/${record.courseId}`
                        : profileTo;

                      return (
                        <tr key={record.id}>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              {record.user?.avatarUrl ? (
                                <img src={record.user.avatarUrl} alt="" className="lms-gradebook__avatar" />
                              ) : (
                                <div className="lms-gradebook__avatar" aria-hidden="true">
                                  {initials(name) || '?'}
                                </div>
                              )}
                              <div className="min-w-0">
                                {nameTo ? (
                                  <Link to={nameTo} className="lms-gradebook__name">
                                    {name}
                                  </Link>
                                ) : (
                                  <span className="lms-gradebook__name">{name}</span>
                                )}
                                <div className="lms-gradebook__muted">{record.user?.email ?? '—'}</div>
                              </div>
                            </div>
                          </th>
                          <td className="lms-gradebook__td">
                            {record.course ? (
                              <span className="inline-flex flex-wrap items-center gap-2">
                                {record.course.name}
                                {record.course.trashed ? <Badge variant="danger">Eliminado</Badge> : null}
                              </span>
                            ) : (
                              <span className="lms-gradebook__muted">Curso no disponible</span>
                            )}
                          </td>
                          <td className="lms-gradebook__td">
                            <div className="lms-progress-admin__progress">
                              <span className="lms-gradebook__emphasis">{rounded}%</span>
                              <div
                                className="lms-progress-admin__bar"
                                role="progressbar"
                                aria-valuenow={rounded}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-label={`Progreso ${rounded}%`}
                              >
                                <div
                                  className={`lms-progress-admin__bar-fill lms-progress-admin__bar-fill--${barMod(record.status)}`}
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="lms-gradebook__td">
                            <Badge variant={statusVariant(record.status)}>
                              {STATUS_LABELS[record.status ?? ''] ?? record.status ?? '—'}
                            </Badge>
                          </td>
                          <td className="lms-gradebook__td lms-gradebook__td--nowrap">
                            {lastAccess ? (
                              <div>
                                <div>{lastAccess.toLocaleDateString('es')}</div>
                                <div className="lms-gradebook__muted">
                                  {lastAccess.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            ) : (
                              <span className="lms-gradebook__muted">—</span>
                            )}
                          </td>
                          <td className="lms-gradebook__td lms-gradebook__td--actions">
                            {canDetail || profileTo ? (
                              <div className="flex items-center justify-end gap-1">
                                {canDetail ? (
                                  <Link
                                    to={`/progreso/${record.userId}/${record.courseId}`}
                                    className="lms-gradebook__action"
                                    title="Ver detalle"
                                    aria-label={`Ver progreso de ${name} en ${record.course?.name ?? 'curso'}`}
                                  >
                                    <span className="material-symbols-outlined" aria-hidden="true">
                                      visibility
                                    </span>
                                  </Link>
                                ) : null}
                                {profileTo ? (
                                  <Link
                                    to={profileTo}
                                    className="lms-gradebook__action"
                                    title="Ver perfil"
                                    aria-label={`Ver perfil de ${name}`}
                                  >
                                    <span className="material-symbols-outlined" aria-hidden="true">
                                      person
                                    </span>
                                  </Link>
                                ) : null}
                              </div>
                            ) : (
                              <span className="lms-gradebook__muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lms-course-forum-card__footer">
                <span className="lms-course-forum-card__meta">
                  Mostrando <strong>1</strong> a <strong>{records.length}</strong> de{' '}
                  <strong>{records.length}</strong> resultados
                </span>
                <div className="lms-course-forum-card__pager">
                  <button type="button" className="lms-course-forum-card__pager-btn" aria-label="Página anterior" disabled>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <span className="lms-course-forum-card__pager-current">1</span>
                  <button type="button" className="lms-course-forum-card__pager-btn" aria-label="Página siguiente" disabled>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
